"use client";

import { useEffect, useRef, type ReactNode } from "react";
import { usePathname, useRouter } from "next/navigation";
import { shellLinkClick } from "@/lib/clientNav";
import { useSession } from "next-auth/react";
import { useOnlineStatus } from "@/hooks/useOnlineStatus";
import { loginUrl } from "@/lib/pwa-refresh";
import { RefreshBar } from "@/components/finance/RefreshBar";
import { FinanceSkeleton } from "@/components/finance/FinanceSkeleton";
import { useFinanceReady } from "@/lib/finance/finance-data-context";

// The mobile bottom bar SCROLLS horizontally (Oct 2026): eight tabs no longer
// fit a phone width without squeezing, so each tab keeps a fixed width and
// its full label, and the active one is scrolled into view on navigation.
const NAV: { href: string; label: string; icon: string }[] = [
  { href: "/finance", label: "Overview", icon: "M3 12l9-8 9 8M5 10v10h5v-6h4v6h5V10" },
  { href: "/finance/accounts", label: "Accounts", icon: "M3 7a2 2 0 012-2h14a2 2 0 012 2v10a2 2 0 01-2 2H5a2 2 0 01-2-2V7zm0 3h18" },
  { href: "/finance/net-worth", label: "Net Worth", icon: "M3 17l5-5 4 3 6-8 3 3M3 21h18" },
  { href: "/finance/transactions", label: "Transactions", icon: "M4 7h13m0 0l-3-3m3 3l-3 3M20 17H7m0 0l3-3m-3 3l3 3" },
  { href: "/finance/trends", label: "Trends", icon: "M5 21V9m7 12V3m7 18v-8" },
  { href: "/finance/cash-flow", label: "Cash Flow", icon: "M12 3v18m0-18l-4 4m4-4l4 4m-4 14l-4-4m4 4l4-4" },
  { href: "/finance/savings", label: "Savings", icon: "M8 3h8M9 3v3a5 5 0 00-3 4.6V19a2 2 0 002 2h8a2 2 0 002-2v-8.4A5 5 0 0015 6V3" },
  { href: "/finance/projections", label: "Projections", icon: "M3 17l5-5 4 3 5-7m0 0h-4m4 0v4" },
]

// House glyph — the "All apps" home affordance, shared with the trip planner
// header so both apps point back to the launcher the same way.
const HOME_ICON = "M3 12l9-8 9 8M5 10v10h5v-6h4v6h5V10";

function Icon({ d, className = "h-5 w-5" }: { d: string; className?: string }) {
  return (
    <svg
      viewBox="0 0 24 24"
      className={className}
      fill="none"
      stroke="currentColor"
      strokeWidth={1.8}
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden
    >
      <path d={d} />
    </svg>
  );
}

export function FinanceChrome({ children }: { children: ReactNode }) {
  const pathname = usePathname();
  const router = useRouter();
  const { data: session, status } = useSession();
  const ready = useFinanceReady();
  const isOnline = useOnlineStatus();

  // Same gate as TripChrome: a signed-out visitor who is ONLINE and has no
  // data painted goes to the login screen (with this path as the callback)
  // instead of staring at skeletons while the API 401s underneath. Never
  // while offline, and never once cached data is on screen (lesson 6) — the
  // 401 from a Refresh handles that case via handleUnauthorized.
  useEffect(() => {
    if (status === "unauthenticated" && isOnline && !ready) {
      router.replace(loginUrl(pathname));
    }
  }, [status, isOnline, ready, pathname, router]);

  const isActive = (href: string) =>
    href === "/finance" ? pathname === "/finance" : pathname.startsWith(href);

  // Keep the active tab visible in the scrolling bottom bar — but only nudge
  // the bar when the tab is actually clipped, so a tab the user just tapped
  // (visible by definition) never jumps under their thumb.
  const barRef = useRef<HTMLElement>(null);
  useEffect(() => {
    const bar = barRef.current;
    const el = bar?.querySelector<HTMLElement>('[aria-current="page"]');
    if (!bar || !el) return;
    const left = el.offsetLeft;
    const right = left + el.offsetWidth;
    if (left < bar.scrollLeft) bar.scrollLeft = Math.max(0, left - 12);
    else if (right > bar.scrollLeft + bar.clientWidth) bar.scrollLeft = right - bar.clientWidth + 12;
  }, [pathname]);

  return (
    <div className="fin min-h-dvh bg-surface-2 text-ink lg:flex">
      {/* Desktop sidebar */}
      <aside className="fixed inset-y-0 left-0 z-20 hidden w-56 flex-col border-r border-edge bg-surface-1 p-4 lg:flex">
        <div className="mb-5 flex items-center gap-2 px-2">
          <span className="text-2xl" aria-hidden>
            🌿
          </span>
          <span className="text-lg font-semibold tracking-tight">Finances</span>
        </div>
        <nav className="flex flex-1 flex-col gap-1" aria-label="Finance">
          {NAV.map((item) => (
            <a
              key={item.href}
              href={item.href}
              onClick={shellLinkClick}
              className={`flex items-center gap-3 rounded-xl px-3 py-2 text-sm font-medium transition-colors ${
                isActive(item.href)
                  ? "bg-surface-3 text-ink"
                  : "text-ink-2 hover:bg-surface-3/60 hover:text-ink"
              }`}
            >
              <Icon d={item.icon} />
              {item.label}
            </a>
          ))}
          {/* Back to the launcher — same home glyph as the mobile header,
              styled like the tabs and pushed to the bottom of the nav block. */}
          {/* Cross-shell jump: plain <a> — a real navigation the SW serves
              from the precache (no prefetch, no network). */}
          <a
            href="/"
            aria-label="All apps"
            className="mt-auto flex items-center gap-3 rounded-xl px-3 py-2 text-sm font-medium text-ink-2 transition-colors hover:bg-surface-3/60 hover:text-ink"
          >
            <Icon d={HOME_ICON} />
            All apps
          </a>
        </nav>
        <div className="mt-4 border-t border-edge pt-3">
          <RefreshBar />
        </div>
        <div className="mt-3 border-t border-edge pt-3">
          <div className="min-w-0 truncate text-xs font-medium text-ink">
            {session?.user?.name ?? session?.user?.email ?? ""}
          </div>
        </div>
      </aside>

      {/* Opaque fill behind the iOS status bar / notch. That band sits above
          the sticky header and is otherwise transparent, so scrolled content
          leaks up into it — this covers it. */}
      <div
        className="fixed inset-x-0 top-0 z-30 bg-surface-1 lg:hidden"
        style={{ height: "env(safe-area-inset-top)" }}
        aria-hidden
      />

      {/* Mobile top bar — fixed in place. The <body> already pads by
          env(safe-area-inset-top), so the header must NOT add it again (that
          double-padded and, since the body padding scrolls away, made the bar
          appear to collapse). Sticking at top:safe-area keeps it pinned just
          below the iOS status bar at every scroll position. */}
      <header
        className="sticky z-20 flex items-center justify-between border-b border-edge bg-surface-1 px-4 py-3 lg:hidden"
        style={{ top: "env(safe-area-inset-top)" }}
      >
        <div className="flex items-center gap-2">
          <span className="text-xl" aria-hidden>
            🌿
          </span>
          <span className="font-semibold tracking-tight">Finances</span>
        </div>
        <div className="flex items-center gap-2">
          <RefreshBar />
          <a
            href="/"
            aria-label="All apps"
            className="rounded-full p-2 text-ink-2 hover:bg-surface-3 hover:text-ink"
          >
            <Icon d={HOME_ICON} />
          </a>
        </div>
      </header>

      {/* Main content */}
      <main className="min-w-0 flex-1 px-4 pb-24 pt-4 sm:px-6 lg:ml-56 lg:pb-8 lg:pt-6">
        <div className="mx-auto max-w-6xl">
          {ready ? children : <FinanceSkeleton />}
        </div>
      </main>

      {/* Mobile bottom nav — a horizontally scrolling row of fixed-width tabs
          (scrollbar hidden; the active tab is kept in view above). */}
      <nav
        ref={barRef}
        aria-label="Finance"
        className="scrollbar-hide fixed inset-x-0 bottom-0 z-20 flex items-stretch overflow-x-auto border-t border-edge bg-surface-1 lg:hidden"
        style={{ paddingBottom: "env(safe-area-inset-bottom)", WebkitOverflowScrolling: "touch" }}
      >
        {NAV.map((item) => (
          <a
            key={item.href}
            href={item.href}
            onClick={shellLinkClick}
            aria-current={isActive(item.href) ? "page" : undefined}
            className={`flex w-[4.75rem] flex-none flex-col items-center gap-0.5 px-1 py-2 text-center text-[10px] font-medium leading-tight ${
              isActive(item.href) ? "text-accent-ink" : "text-ink-3"
            }`}
          >
            <Icon d={item.icon} className="h-5 w-5" />
            <span className="truncate w-full">{item.label}</span>
          </a>
        ))}
      </nav>
    </div>
  );
}
